/* eslint-disable upright/no-primer-flash */
import { XIcon } from '@primer/octicons-react';
import { FlashProps, IconButton, Flash as PrimerFlash, Tooltip } from '@primer/react';
import { themeGet } from '@upright/ui/common/generic/theme/custom-theme';
import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FlashPlatformVariant, flashPlatformVariants } from './variants';
import { useTimeout } from '@upright/ui/common/generic/hooks/external/use-timeout';
import { isBannerDismissed, saveBannerDismissal } from '../../../utilities/storage';

/* Our Flash extends Primer's Flash with custom styling and closing functionality. Use it instead of Primer's default.

   Directions:
   1. Choose the appropriate variant from default (for neutral messages), success, warning, and danger.
   2. Choose whether the alert should be dismissible.
   3. Add an appropriate icon. See 'internal/design' playground page for examples.

   Best practices:
   * Use flash alerts for things that require the user's attention but that are not initiated by the user.
   * Flash alerts can disrupt the user experience, so use them sparingly and only when necessary
   * Only show one flash alert at a time
   * Be concise and clear in messaging

*/

type FlashPlatformProps = Omit<FlashProps, 'variant'> & {
  dismissable?: boolean;
  flashIdForDismissalTracking?: string;
  icon?: ReactNode;
  children: ReactNode;
  variant?: FlashPlatformVariant;
  timeout?: number;
  /** Called after the user clicks the dismiss (X) button. Lets the parent clear any
   *  state that gates the Flash on its rendering side, so a re-render with new content
   *  produces a freshly-mounted Flash rather than a stuck "already-dismissed" one. */
  onDismiss?: () => void;
};

export const Flash = (props: FlashPlatformProps) => {
  const isInitiallyDismissedInLocalStorage = props.flashIdForDismissalTracking
    ? isBannerDismissed(props.flashIdForDismissalTracking)
    : false;
  const [isShown, setIsShown] = useState(!isInitiallyDismissedInLocalStorage);

  useEffect(() => {
    setIsShown(!isInitiallyDismissedInLocalStorage);
  }, [isInitiallyDismissedInLocalStorage]);

  function closeFlash() {
    if (props.flashIdForDismissalTracking) {
      saveBannerDismissal(props.flashIdForDismissalTracking);
    }
    setIsShown(false);
    props.onDismiss?.();
  }

  useTimeout(() => {
    closeFlash();
  }, props.timeout ?? null);

  const adaptedProps = {
    style: props.style,
    className: props.className,
    dismissable: props.dismissable ? 'true' : 'false',
    variant: props.variant as ComponentProps<typeof PrimerFlash>['variant'],
  };

  return isShown ? (
    <StyledFlash {...adaptedProps}>
      <Main>
        <IconAndText>
          {props.icon}
          {props.children}
        </IconAndText>
        {props.dismissable && (
          <Dismiss>
            <Tooltip aria-label="Dismiss message" direction="w" text="Dismiss message">
              <IconButton
                variant="invisible"
                aria-label="Dismiss message"
                icon={XIcon}
                onClick={closeFlash}
                sx={{ svg: { margin: 0 }, ':hover': { bg: 'transparent' } }}
              />
            </Tooltip>
          </Dismiss>
        )}
      </Main>
    </StyledFlash>
  ) : (
    <></>
  );
};

const IconAndText = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__xs);
  padding-top: var(--space__lg);
  padding-bottom: var(--space__lg);

  b {
    font-weight: 500;
  }
`;

const Main = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
`;

const StyledFlash = styled(PrimerFlash)`
  font-size: ${themeGet('fontSizes.1')};
  ${flashPlatformVariants};
  padding-left: var(--space__lg);
  padding-right: var(--space__lg);
  padding-top: 0;
  padding-bottom: 0;
`;

const Dismiss = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space__lg);
`;
